import { createContext, Dispatch, useReducer, useState } from "react";
import { AddTaskModal } from "shared/components";
import { TaskPhase } from "shared/types/task.ts";
import { GenerateDocumentModal } from "shared/components/searchBar/components/generateDocumentModal.tsx";
import KayleeModal from "screens/kaylee/kaylee-modal.tsx";
import KayleeTenantModal from "screens/kaylee/kaylee-tenant.tsx";
import { useDisclosure } from "@heroui/react";
import AddAcquisition from "components/addAcquisition/addAcquisition";
import { useNavigate, useLocation } from "react-router-dom";
import { FileUploadField, UploadType } from "../uploadFileModal/uploadFileModal";
import { QuickAddTicketModal } from "screens/helpdesk/components/quickAddTicketModal";
import QuickAddInsuranceIssueModal from "screens/buildings/components/insurances/QuickAddInsuranceIssueModal";
import StartCommunicationModal from "../communication/startCommunicationModal";
import { MassMailModal } from "../MassMailModal";
import { UniversalAiAssistant } from "screens/helpdesk/components/AiAssistant";

type MenuState = {
  dispatch: Dispatch<{ type: string; payload: boolean }>;
  handleKayleeOpenChange: () => void;
  handleTenantKayleeOpenChange: () => void;
  onOpenAcquisition: () => void;
  onOpenChangeAcquisition: (open: boolean) => void;
  openDocumentUpload: () => void;
  openPhotoUpload: () => void;
  openAiAssistant: () => void;
};
export const MenuContext = createContext<MenuState>({
  dispatch: () => {
    console.warn("dispatch function not initialized");
  },
  handleKayleeOpenChange: () => {
    console.warn("handleKayleeOpenChange function not initialized");
  },
  handleTenantKayleeOpenChange: () => {
    console.warn("handleTenantKayleeOpenChange function not initialized");
  },
  onOpenAcquisition: () => {
    console.warn("handleTenantKayleeOpenChange function not initialized");
  },
  onOpenChangeAcquisition: () => {
    console.warn("handleTenantKayleeOpenChange function not initialized");
  },
  openDocumentUpload: () => {
    console.warn("openDocumentUpload function not initialized");
  },
  openPhotoUpload: () => {
    console.warn("openPhotoUpload function not initialized");
  },
  openAiAssistant: () => {
    console.warn("openAiAssistant function not initialized");
  },
});

interface MenuProviderProps {
  children: React.ReactNode;
}

const reducer = (
  state: {
    isGenerateDocumentModalOpen: boolean;
    isTaskModalOpen: boolean;
    isHelpdeskModalOpen: boolean;
    isInsuranceCaseModalOpen: boolean;
    isStartCommunicationModalOpen: boolean;
    isMassMailModalOpen: boolean;
    isAiAssistantModalOpen: boolean;
  },
  action: {
    type: string;
    payload: boolean;
  },
) => {
  switch (action.type) {
    case "taskModal":
      return {
        isGenerateDocumentModalOpen: false,
        isTaskModalOpen: action.payload,
        isHelpdeskModalOpen: false,
        isInsuranceCaseModalOpen: false,
        isStartCommunicationModalOpen: false,
        isMassMailModalOpen: false,
      };
    case "generateDocumentModal":
      return {
        isTaskModalOpen: false,
        isGenerateDocumentModalOpen: action.payload,
        isHelpdeskModalOpen: false,
        isInsuranceCaseModalOpen: false,
        isStartCommunicationModalOpen: false,
        isMassMailModalOpen: false,
      };
    case "helpdeskModal":
      return {
        isHelpdeskModalOpen: action.payload,
        isTaskModalOpen: false,
        isGenerateDocumentModalOpen: false,
        isInsuranceCaseModalOpen: false,
        isStartCommunicationModalOpen: false,
        isMassMailModalOpen: false,
      };
    case "addInsuranceCaseModal":
      return {
        isHelpdeskModalOpen: false,
        isTaskModalOpen: false,
        isGenerateDocumentModalOpen: false,
        isInsuranceCaseModalOpen: action.payload,
        isStartCommunicationModalOpen: false,
        isMassMailModalOpen: false,
      };
    case "startCommunicationModal":
      return {
        isHelpdeskModalOpen: false,
        isTaskModalOpen: false,
        isGenerateDocumentModalOpen: false,
        isInsuranceCaseModalOpen: false,
        isStartCommunicationModalOpen: action.payload,
        isMassMailModalOpen: false,
      };
    case "massMailModal":
      return {
        isHelpdeskModalOpen: false,
        isTaskModalOpen: false,
        isDocumentModalOpen: false,
        isGenerateDocumentModalOpen: false,
        isPhotoUploadModalOpen: false,
        isInsuranceCaseModalOpen: false,
        isStartCommunicationModalOpen: false,
        isMassMailModalOpen: action.payload,
      };
    case "aiAssistantModal":
      return {
        isHelpdeskModalOpen: false,
        isTaskModalOpen: false,
        isGenerateDocumentModalOpen: false,
        isInsuranceCaseModalOpen: false,
        isStartCommunicationModalOpen: false,
        isMassMailModalOpen: false,
        isAiAssistantModalOpen: action.payload,
      };
    default:
      return state;
  }
};

const initialState = {
  isTaskModalOpen: false,
  isGenerateDocumentModalOpen: false,
  isHelpdeskModalOpen: false,
  isInsuranceCaseModalOpen: false,
  isStartCommunicationModalOpen: false,
  isMassMailModalOpen: false,
  isAiAssistantModalOpen: false,
};

const MenuProvider = ({ children }: MenuProviderProps) => {
  const [state, dispatch] = useReducer(reducer as React.Reducer<any, any>, initialState);
  const [showKaylee, setShowKaylee] = useState(false);
  const [showTenantKaylee, setShowTenantKaylee] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isOpen: isAcquisitionOpen,
    onOpen: onOpenAcquisition,
    onOpenChange: onOpenChangeAcquisition,
  } = useDisclosure();

  // Check if we're on the tasks page - prevent rendering AddTaskModal here to avoid focus conflicts
  const isOnTasksPage = location.pathname === '/tasks' || location.pathname.startsWith('/tasks/');


  const handleKayleeOpenChange = () => {
    setShowKaylee(!showKaylee);
  };

  const handleTenantKayleeOpenChange = () => {
    setShowTenantKaylee(!showTenantKaylee);
  };

  const openDocumentUpload = () => {
    navigate("/upload-files", {
      state: {
        uploadType: UploadType.FILES,
        skipFields: [],
        variant: "extended"
      }
    });
  };

  const openPhotoUpload = () => {
    navigate("/upload-files", {
      state: {
        uploadType: UploadType.PICTURES,
        skipFields: [
          FileUploadField.DOCUMENT_DIGITAL_COMPATIBILITY,
          FileUploadField.STATUS,
          FileUploadField.SIGN_STATUS,
          FileUploadField.COUNTERPARTIES,
          FileUploadField.REFERENCE_CODE,
          FileUploadField.EXPIRATION_DATE
        ],
        variant: "extended"
      }
    });
  };

  const openAiAssistant = () => {
    dispatch({ type: "aiAssistantModal", payload: true });
  };


  return (
    <MenuContext.Provider
      value={{
        dispatch,
        handleKayleeOpenChange,
        handleTenantKayleeOpenChange,
        onOpenAcquisition,
        onOpenChangeAcquisition,
        openDocumentUpload,
        openPhotoUpload,
        openAiAssistant,
      }}
    >
      {children}
      <QuickAddTicketModal
        isOpen={state.isHelpdeskModalOpen}
        onClose={() => dispatch({ type: "helpdeskModal", payload: false })}
      />
      <StartCommunicationModal
        isOpen={state.isStartCommunicationModalOpen}
        onOpenChange={(value: any) =>
          dispatch({ type: "startCommunicationModal", payload: value })
        }
      />
      <QuickAddInsuranceIssueModal
        isOpen={state.isInsuranceCaseModalOpen}
        onClose={() => dispatch({ type: "addInsuranceCaseModal", payload: false } as any)}
        onCreated={() => dispatch({ type: "addInsuranceCaseModal", payload: false } as any)}
      />
      {/* Only render AddTaskModal if NOT on tasks page to avoid focus trap conflicts with the tasks page's own modal */}
      {!isOnTasksPage && (
        <AddTaskModal
          phase={TaskPhase.INBOX}
          isOpen={state.isTaskModalOpen}
          onOpenChange={(value) =>
            dispatch({ type: "taskModal", payload: value })
          }
          onClose={() => dispatch({ type: "taskModal", payload: false })}
        />
      )}
      <GenerateDocumentModal
        isOpen={state.isGenerateDocumentModalOpen}
        onOpenChange={(value) =>
          dispatch({ type: "generateDocumentModal", payload: value })
        }
      />
      <KayleeModal isOpen={showKaylee} onOpenChange={handleKayleeOpenChange} />
      <KayleeTenantModal
        isOpen={showTenantKaylee}
        onOpenChange={handleTenantKayleeOpenChange}
      />
      <AddAcquisition
        isOpen={isAcquisitionOpen}
        onOpenChange={onOpenChangeAcquisition}
      />
      <MassMailModal
        isOpen={state.isMassMailModalOpen}
        onClose={() => dispatch({ type: "massMailModal", payload: false })}
      />
      <UniversalAiAssistant
        isOpen={state.isAiAssistantModalOpen}
        onClose={() => dispatch({ type: "aiAssistantModal", payload: false })}
        onSuccess={() => dispatch({ type: "aiAssistantModal", payload: false })}
      />
    </MenuContext.Provider>
  );
};

export default MenuProvider;
